/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import { formatPhoneNumber, displayPhoneNumber, displayAccountNumber } from '../../utils/formatUtils';
import PhoneInput from '../shared/PhoneInput';
import { calculateDays, isAssignmentActive, clampEndDateToAssignment } from '../../utils/invoiceCalculations';
import { lineCost, formatRate, formatBilledUnits, billedUnits } from '../../utils/ratePricing';

// Default bill_to company when the user has no company set on their profile:
// NCSU users default to 'NCSU', everyone else is treated as a commercial client.
const getCompanyFallback = (isNcsu) => (isNcsu ? 'NCSU' : 'Commercial Client');

// Helper function to format date as YYYY-MM-DD in LOCAL timezone
const formatLocalDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to parse a date from database as a local date
const parseAsLocalDate = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    return new Date(dateValue.getUTCFullYear(), dateValue.getUTCMonth(), dateValue.getUTCDate(), 12, 0, 0, 0);
  }
  const d = new Date(dateValue);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0, 0);
};

// Parse date string (YYYY-MM-DD from input) as local date - avoids timezone shift
const parseInputDateAsLocal = (dateStr) => {
  if (!dateStr) return null;
  const str = String(dateStr).split('T')[0];
  const parts = str.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day, 12, 0, 0, 0);
  }
  return new Date(dateStr);
};

const EditInvoice = ({ open, onClose, invoice, project, onInvoiceUpdated }) => {
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingInvoiceData, setLoadingInvoiceData] = useState(true);

  // Invoice data state
  const [invoiceData, setInvoiceData] = useState(null);
  
  // State for billing period
  const [billStartDate, setBillStartDate] = useState('');
  const [billEndDate, setBillEndDate] = useState('');
  
  // State for Bill To information
  const [billTo, setBillTo] = useState({
    name: '',
    company: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: ''
  });
  const [selectedBillToUser, setSelectedBillToUser] = useState('');
  const [loadingBillToUser, setLoadingBillToUser] = useState(false);
  
  // State for invoice comment
  const [invoiceComment, setInvoiceComment] = useState('');
  
  // State for adjustments
  const [adjustments, setAdjustments] = useState([]);
  
  // State for distribution type
  const [distributionType, setDistributionType] = useState('user_based');
  
  // State for user distributions
  const [userDistributions, setUserDistributions] = useState([]);
  
  // State for account distributions
  const [accountDistributions, setAccountDistributions] = useState([]);
  
  // State for chamber usage
  const [chamberUsage, setChamberUsage] = useState([]);
  const [excludedChamberRows, setExcludedChamberRows] = useState([]);
  
  // State for resource usage
  const [resourceUsage, setResourceUsage] = useState([]);
  const [excludedResourceRows, setExcludedResourceRows] = useState([]);
  
  // State for previous balances
  const [previousBalances, setPreviousBalances] = useState([]);
  
  // Account mapping state
  const [accountMapping, setAccountMapping] = useState({
    facility_id: '',
    account_number: ''
  });
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityAccounts, setSelectedFacilityAccounts] = useState([]);

  // All users for adding to distributions
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState('');
  
  // Account addition states
  const [newAccountNumber, setNewAccountNumber] = useState('');
  const [newSourceOfFunds, setNewSourceOfFunds] = useState('');
  const [selectedUserForNewAccount, setSelectedUserForNewAccount] = useState('');
  const [selectedUserAccounts, setSelectedUserAccounts] = useState([]);
  const [loadingUserAccounts, setLoadingUserAccounts] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [isNewAccount, setIsNewAccount] = useState(false);

  // Project state for updates
  const [currentProject, setCurrentProject] = useState(null);

  // Fetch all users
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await axios.get('/api/users/');
        setAllUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchAllUsers();
  }, []);

  // Set current project when dialog opens
  useEffect(() => {
    if (open && project) {
      setCurrentProject(project);
    }
  }, [open, project]);

  // Fetch facilities for account mapping
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await axios.get('/api/facilities');
        setFacilities(response.data);
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    };
    fetchFacilities();
  }, []);

  // Fetch invoice details when dialog opens
  useEffect(() => {
    if (open && invoice && project) {
      fetchInvoiceDetails();
    }
  }, [open, invoice, project]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoadingInvoiceData(true);
      
      // Get the invoice document ID and invoice_id
      const docId = invoice._id || invoice.parentId;
      const invoiceId = invoice.invoice_id;
      
      // Fetch full invoice details with chamber/resource usage
      const response = await axios.get(`/api/invoices/${docId}?invoice_id=${invoiceId}&populate=true`);
      const fullInvoice = response.data;
      
      setInvoiceData(fullInvoice);
      
      // Populate billing period
      if (fullInvoice.billing_period) {
        const startDate = parseAsLocalDate(fullInvoice.billing_period.start_date);
        const endDate = parseAsLocalDate(fullInvoice.billing_period.end_date);
        setBillStartDate(formatLocalDate(startDate));
        setBillEndDate(formatLocalDate(endDate));
      }
      
      // Populate bill_to
      if (fullInvoice.bill_to) {
        setBillTo(fullInvoice.bill_to);
        setSelectedBillToUser(fullInvoice.bill_to.email || '');
      }
      
      // Populate comment
      setInvoiceComment(fullInvoice.comment || '');
      
      // Populate adjustments
      if (fullInvoice.adjustments && fullInvoice.adjustments.length > 0) {
        setAdjustments(fullInvoice.adjustments.map(adj => ({
          amount: adj.amount || '',
          comments: adj.description || ''
        })));
      } else {
        setAdjustments([]);
      }
      
      // Populate distribution type and distributions
      setDistributionType(fullInvoice.distribution_type || 'user_based');
      
      if (fullInvoice.amount_distribution && fullInvoice.amount_distribution.length > 0) {
        if (fullInvoice.distribution_type === 'account_based') {
          setAccountDistributions(fullInvoice.amount_distribution.map(dist => {
            const display = dist.display_name || '';
            const sourceParts = display.split(' - ');
            const source_of_funds = sourceParts.length > 1 ? sourceParts.slice(1).join(' - ') : '';
            const uid = dist.user_id
              ? (typeof dist.user_id === 'object' ? dist.user_id._id : dist.user_id)
              : null;
            let user_email = '';
            if (uid && project?.users) {
              const pu = project.users.find(u =>
                (u.user?._id || u.user)?.toString() === uid.toString()
              );
              user_email = pu?.email || '';
            }
            return {
              // Persist the original sub-document _id so the backend edit
              // handler can identify this as an existing row (and reject
              // attempts to mutate identifier / user_id on it).
              _id: dist._id || undefined,
              _isExisting: true,
              account_number: dist.identifier,
              source_of_funds,
              user_id: uid || undefined,
              user_email,
              percent_share: String(dist.percent_share || 0)
            };
          }));
          setUserDistributions([]);
        } else {
          setUserDistributions(fullInvoice.amount_distribution.map(dist => ({
            user_id: dist.user_id || dist.identifier,
            user_name: dist.display_name,
            percent_share: String(dist.percent_share || 0)
          })));
          setAccountDistributions([]);
        }
      }
      
      // Populate previous balances
      if (fullInvoice.previous_balance && fullInvoice.previous_balance.length > 0) {
        setPreviousBalances(fullInvoice.previous_balance.map(pb => ({
          ...pb,
          included: true
        })));
      } else {
        setPreviousBalances([]);
      }
      
      // Populate account mapping
      if (fullInvoice.account_mapping) {
        setAccountMapping({
          facility_id: fullInvoice.account_mapping.facility_id || '',
          account_number: fullInvoice.account_mapping.account_number || ''
        });
        
        // Set facility accounts
        const facility = facilities.find(f => f._id === fullInvoice.account_mapping.facility_id);
        setSelectedFacilityAccounts(facility?.accounts_receivable || []);
      }
      
      // Populate chamber usage from the invoice data
      if (fullInvoice.chamber_usage && fullInvoice.chamber_usage.length > 0) {
        const usageItems = [];
        let assignmentIndex = 0;
        fullInvoice.chamber_usage.forEach(cu => {
          const chamberName = cu.chamber?.chamber || 'Unknown Compartment';
          const chamberId = cu.chamber?._id || cu.chamber;
          
          if (cu.assignments && cu.assignments.length > 0) {
            cu.assignments.forEach(assignment => {
              // Use billed_period_id for true uniqueness (each billed period is unique)
              // Fall back to assignment._id + index, or chamber + rate + quantity + index
              const assignmentKey = assignment.billed_period_id || 
                (assignment._id ? `${assignment._id}_${assignmentIndex}` : null) ||
                `${chamberId}_${assignment.rate_id || 'default'}_${assignment.quantity || 0}_${assignmentIndex}`;
              
              usageItems.push({
                uniqueKey: assignmentKey,
                chamber_id: chamberId,
                chamber_name: chamberName,
                assignment_id: assignment._id,
                billed_period_id: assignment.billed_period_id,
                rate_id: assignment.rate_id,
                rate_name: assignment.rate_name || assignment.rate_id,
                start_date: formatLocalDate(parseAsLocalDate(assignment.start_date)),
                end_date: formatLocalDate(parseAsLocalDate(assignment.end_date)),
                quantity: assignment.quantity,
                rate: assignment.rate,
                rate_basis: assignment.rate_basis || 'day',
                total_days: assignment.total_days,
                total_cost: assignment.total_cost,
                // Assignment status + actual end date: used to label the row and
                // to avoid extending a completed assignment's billing end date
                // past its real end date when the bill end date changes.
                assignment_status: assignment.assignment_status,
                assignment_end_date: assignment.assignment_end_date
              });
              assignmentIndex++;
            });
          }
        });
        setChamberUsage(usageItems);
      } else {
        setChamberUsage([]);
      }
      
      // Populate resource usage from the invoice data
      if (fullInvoice.resource_usage && fullInvoice.resource_usage.length > 0) {
        const resourceItems = fullInvoice.resource_usage.map((ru, index) => ({
          uniqueKey: ru.invoiced_quantity_id ||
            (ru.resource?._id || ru.resource_id ? `${ru.resource?._id || ru.resource_id}_${index}` : `resource_${index}`),
          resource_id: ru.resource?._id || ru.resource_id,
          resource_name: ru.resource?.resource || 'Unknown Resource',
          rate_display_name: ru.rate_display_name || '',
          rate_basis: ru.rate_basis || 'unit',
          quantity: ru.quantity,
          rate: ru.rate,
          total_cost: ru.total_cost
        }));
        setResourceUsage(resourceItems);
      } else {
        setResourceUsage([]);
      }
      
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast.error('Failed to load invoice details');
    } finally {
      setLoadingInvoiceData(false);
    }
  };

  // Handle Bill To user selection
  const handleBillToUserSelect = async (userEmail) => {
    setSelectedBillToUser(userEmail);
    
    if (!userEmail) {
      setBillTo({
        name: '',
        company: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: ''
      });
      return;
    }
    
    const projectUser = project?.users?.find(u => u.email === userEmail);
    if (projectUser) {
      const fullName = `${projectUser.first_name || ''} ${projectUser.last_name || ''}`.trim();
      
      setLoadingBillToUser(true);
      try {
        const response = await axios.get(`/api/users/profile/by-email/${encodeURIComponent(userEmail)}`);
        const userData = response.data;
        
        setBillTo({
          name: fullName,
          company: userData.company || getCompanyFallback(userData.is_ncsu),
          address_line1: userData.address_line1 || userData.address || '',
          address_line2: userData.address_line2 || '',
          city: userData.city || '',
          state: userData.state || '',
          zip: userData.zip || userData.zip_code || '',
          phone: userData.contact_number || userData.phone || '',
          email: userEmail
        });
      } catch (error) {
        setBillTo({
          name: fullName,
          company: getCompanyFallback(projectUser.is_ncsu),
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          email: userEmail
        });
      } finally {
        setLoadingBillToUser(false);
      }
    }
  };

  // Update bill start date to the earliest start date among non-excluded chamber usage rows
  const updateBillStartDateFromChamberUsage = (usageItems = chamberUsage, excludedSet = excludedChamberRows) => {
    let earliestStartDate = null;
    usageItems
      .filter(item => item && !excludedSet.includes(item.uniqueKey))
      .forEach(item => {
        const startDate = item.start_date;
        if (startDate) {
          const dateObj = parseInputDateAsLocal(startDate);
          if (dateObj && (!earliestStartDate || dateObj < earliestStartDate)) {
            earliestStartDate = dateObj;
          }
        }
      });
    if (earliestStartDate) {
      setBillStartDate(formatLocalDate(earliestStartDate));
    }
  };

  // Update bill end date to the latest end date among non-excluded chamber usage rows
  const updateBillEndDateFromChamberUsage = (usageItems = chamberUsage, excludedSet = excludedChamberRows) => {
    let latestEndDate = null;
    usageItems
      .filter(item => item && !excludedSet.includes(item.uniqueKey))
      .forEach(item => {
        const endDate = item.end_date;
        if (endDate) {
          const dateObj = parseInputDateAsLocal(endDate);
          if (dateObj && (!latestEndDate || dateObj > latestEndDate)) {
            latestEndDate = dateObj;
          }
        }
      });
    if (latestEndDate) {
      setBillEndDate(formatLocalDate(latestEndDate));
    }
  };

  // When bill start date changes: update all non-excluded chamber usage start dates to match
  const handleBillStartDateChange = (newStartDateValue) => {
    setBillStartDate(newStartDateValue);
    if (!newStartDateValue) return;
    setChamberUsage(prev => prev.map(item => {
      if (excludedChamberRows.includes(item.uniqueKey)) return item;
      const updated = { ...item, start_date: newStartDateValue };
      if (newStartDateValue && item.end_date) {
        // Shared inclusive, DST-safe day calculation (single source of truth)
        const days = calculateDays(newStartDateValue, item.end_date);
        updated.total_days = days;
        updated.total_cost = lineCost(item.rate, item.quantity, item.rate_basis, newStartDateValue, item.end_date);
      }
      return updated;
    }));
  };

  // When bill end date changes: update non-excluded chamber usage end dates.
  //
  // IMPORTANT: only ACTIVE (ongoing) assignments are billed up to the bill end
  // date. A COMPLETED/Terminated assignment has a fixed end date (e.g. it ended
  // Jan 31) and must NOT be extended to the bill end date - otherwise we would
  // over-bill the client. For active assignments we still clamp to the
  // assignment's own end date so we never bill past when it actually ends.
  const handleBillEndDateChange = (newEndDateValue) => {
    setBillEndDate(newEndDateValue);
    if (!newEndDateValue) return;
    setChamberUsage(prev => prev.map(item => {
      if (excludedChamberRows.includes(item.uniqueKey)) return item;
      // Leave completed/terminated assignments at their existing end date.
      if (!isAssignmentActive(item.assignment_status)) {
        return item;
      }
      // Clamp active assignments so we never bill past their real end date.
      const clampedEnd = clampEndDateToAssignment(newEndDateValue, item.assignment_end_date);
      const clampedEndStr = formatLocalDate(clampedEnd);
      const updated = { ...item, end_date: clampedEndStr };
      if (item.start_date && clampedEndStr) {
        // Shared inclusive, DST-safe day calculation (single source of truth)
        const days = calculateDays(item.start_date, clampedEndStr);
        updated.total_days = days;
        updated.total_cost = lineCost(item.rate, item.quantity, item.rate_basis, item.start_date, clampedEndStr);
      }
      return updated;
    }));
  };

  // Handle chamber usage row changes
  const handleChamberUsageChange = (uniqueKey, field, value) => {
    const updatedUsage = chamberUsage.map(item => {
      if (item.uniqueKey === uniqueKey) {
        const updated = { ...item, [field]: value };
        
        // Recalculate total if dates or rate changed
        if (field === 'start_date' || field === 'end_date' || field === 'rate' || field === 'quantity') {
          const startValue = field === 'start_date' ? value : item.start_date;
          const endValue = field === 'end_date' ? value : item.end_date;
          // Shared inclusive, DST-safe day calculation (single source of truth)
          const days = calculateDays(startValue, endValue);
          const rate = field === 'rate' ? parseFloat(value) || 0 : item.rate;
          const quantity = field === 'quantity' ? parseFloat(value) || 0 : item.quantity;
          
          updated.total_days = days;
          updated.total_cost = lineCost(rate, quantity, item.rate_basis, startValue, endValue);
        }
        
        return updated;
      }
      return item;
    });
    setChamberUsage(updatedUsage);
    // When chamber usage dates change, sync bill period
    if (field === 'end_date') {
      updateBillEndDateFromChamberUsage(updatedUsage);
    } else if (field === 'start_date') {
      updateBillStartDateFromChamberUsage(updatedUsage);
    }
  };

  // Exclude/Include chamber row - also update bill dates when rows change
  const toggleChamberRowExclusion = (uniqueKey) => {
    const newExcluded = excludedChamberRows.includes(uniqueKey)
      ? excludedChamberRows.filter(k => k !== uniqueKey)
      : [...excludedChamberRows, uniqueKey];
    setExcludedChamberRows(newExcluded);
    updateBillStartDateFromChamberUsage(chamberUsage, newExcluded);
    updateBillEndDateFromChamberUsage(chamberUsage, newExcluded);
  };

  // Exclude/Include resource row
  const toggleResourceRowExclusion = (uniqueKey) => {
    setExcludedResourceRows(prev => 
      prev.includes(uniqueKey) 
        ? prev.filter(k => k !== uniqueKey)
        : [...prev, uniqueKey]
    );
  };

  // Handle adjustments
  const handleAddAdjustment = () => {
    setAdjustments([...adjustments, { amount: '', comments: '' }]);
  };
  
  const handleRemoveAdjustment = (index) => {
    const newAdjustments = [...adjustments];
    newAdjustments.splice(index, 1);
    setAdjustments(newAdjustments);
  };
  
  const handleAdjustmentChange = (index, field, value) => {
    const newAdjustments = [...adjustments];
    newAdjustments[index][field] = value;
    setAdjustments(newAdjustments);
  };

  // Handle user distribution changes
  const handleUserDistributionChange = (userId, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 100) {
      toast.error('Percentage must be between 0 and 100');
      return;
    }
    
    const newDistributions = userDistributions.map(dist => {
      const distUserId = typeof dist.user_id === 'string' ? dist.user_id : dist.user_id?._id;
      if (distUserId === userId) {
        return { ...dist, percent_share: value };
      }
      return dist;
    });
    
    setUserDistributions(newDistributions);
  };

  // Handle account distribution percentage (by index — supports duplicate account numbers)
  const handleAccountDistributionChange = (index, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 100) {
      toast.error('Percentage must be between 0 and 100');
      return;
    }
    const newDistributions = accountDistributions.map((dist, i) =>
      i === index ? { ...dist, percent_share: value } : dist
    );
    setAccountDistributions(newDistributions);
  };

  const handleAccountDistributionFieldChange = (index, field, value) => {
    setAccountDistributions(prev =>
      prev.map((dist, i) => (i === index ? { ...dist, [field]: value } : dist))
    );
  };

  // Quick-add from project funding (same user+account uniqueness as GenerateNewInvoice)
  const handleQuickAddFundingAccount = (accountNumber, sourceOfFunds, userId, userEmail) => {
    const exists = accountDistributions.some(
      dist =>
        dist.account_number === accountNumber &&
        (dist.user_id?.toString() === userId?.toString() || dist.user_email === userEmail)
    );
    if (exists) {
      toast.error('Account already in distribution');
      return;
    }
    setAccountDistributions([
      ...accountDistributions,
      {
        account_number: accountNumber,
        source_of_funds: sourceOfFunds,
        user_id: userId,
        user_email: userEmail,
        percent_share: accountDistributions.length === 0 ? '100.00' : '0.00'
      }
    ]);
  };

  // Add user to distributions
  const handleAddUserToDistribution = async () => {
    if (!selectedUserToAdd) return;
    
    const user = allUsers.find(u => u._id === selectedUserToAdd);
    if (!user) return;
    
    // Check if user is already in distributions
    const existingUser = userDistributions.find(dist => {
      const distUserId = typeof dist.user_id === 'string' ? dist.user_id : dist.user_id?._id;
      return distUserId === selectedUserToAdd;
    });
    
    if (existingUser) {
      toast.error('User is already in the distribution');
      return;
    }
    
    try {
      // Add user to the project if not already a member
      const isProjectMember = currentProject?.users?.some(u => {
        const userId = u.user?._id || u.user;
        return userId?.toString() === selectedUserToAdd || u.email === user.email;
      });
      
      if (!isProjectMember) {
        const response = await axios.post(`/api/admin/projects/${currentProject._id}/users`, {
          user_id: selectedUserToAdd,
          role: 'Member'
        });
        setCurrentProject(response.data);
        toast.success(`${user.first_name} ${user.last_name} added to project`);
      }
      
      // Add to distributions with 0%
      const newDistribution = {
        user_id: selectedUserToAdd,
        user_name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        percent_share: '0'
      };
      
      setUserDistributions([...userDistributions, newDistribution]);
      setSelectedUserToAdd('');
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Failed to add user');
    }
  };

  // Remove user from distributions
  const handleRemoveUserFromDistribution = (userId) => {
    const updatedDistributions = userDistributions.filter(dist => {
      const distUserId = typeof dist.user_id === 'string' ? dist.user_id : dist.user_id?._id;
      return distUserId !== userId;
    });
    setUserDistributions(updatedDistributions);
  };

  const handleUserSelectionForNewAccount = async (email) => {
    setSelectedUserForNewAccount(email);
    setSelectedAccountId('');
    setNewAccountNumber('');
    setNewSourceOfFunds('');
    setIsNewAccount(false);

    if (!email) {
      setSelectedUserAccounts([]);
      return;
    }

    try {
      setLoadingUserAccounts(true);
      const encodedEmail = encodeURIComponent(email);
      const response = await axios.get(`/api/users/with-accounts/${encodedEmail}`);
      setSelectedUserAccounts(response.data.account_numbers || []);
    } catch (error) {
      console.error('Error fetching user accounts:', error);
      setSelectedUserAccounts([]);
    } finally {
      setLoadingUserAccounts(false);
    }
  };

  const handleAccountSelection = (accountId) => {
    setSelectedAccountId(accountId);

    if (accountId === 'new') {
      setIsNewAccount(true);
      setNewAccountNumber('');
      setNewSourceOfFunds('');
    } else if (accountId) {
      const selectedAccount = selectedUserAccounts.find(acc => acc._id === accountId);
      if (selectedAccount) {
        setIsNewAccount(false);
        setNewAccountNumber(selectedAccount.number);
        setNewSourceOfFunds(selectedAccount.source);
      }
    } else {
      setIsNewAccount(false);
      setNewAccountNumber('');
      setNewSourceOfFunds('');
    }
  };

  // Add account to project funding + distribution (mirrors GenerateNewInvoice)
  const handleAddAccountToProject = async () => {
    if (!selectedUserForNewAccount || !newAccountNumber || !newSourceOfFunds) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!currentProject?._id) {
      toast.error('Project not loaded');
      return;
    }

    try {
      if (isNewAccount || selectedAccountId === 'new') {
        const userResponse = await axios.get(
          `/api/users/with-accounts/${encodeURIComponent(selectedUserForNewAccount)}`
        );
        const userId = userResponse.data._id;

        await axios.post(`/api/users/${userId}/account-numbers`, {
          number: newAccountNumber,
          source: newSourceOfFunds
        });

        toast.success('New account saved to user profile');
      }

      const projectResponse = await axios.post(`/api/admin/projects/${currentProject._id}/funding`, {
        account_number: newAccountNumber,
        source_of_funds: newSourceOfFunds,
        user_email: selectedUserForNewAccount,
        is_new_account: isNewAccount || selectedAccountId === 'new'
      });

      setCurrentProject(projectResponse.data);

      const newFundingEntry = projectResponse.data.funding?.find(
        f => f.account_number === newAccountNumber && f.user_email === selectedUserForNewAccount
      );
      const projectUser = (projectResponse.data.users || currentProject?.users)?.find(
        u => u.email === selectedUserForNewAccount
      );
      const userId = newFundingEntry?.user_id || projectUser?.user?._id || projectUser?.user;

      const exists = accountDistributions.some(
        dist =>
          dist.account_number === newAccountNumber &&
          (dist.user_id?.toString() === userId?.toString() || dist.user_email === selectedUserForNewAccount)
      );
      if (!exists) {
        setAccountDistributions([
          ...accountDistributions,
          {
            account_number: newAccountNumber,
            source_of_funds: newSourceOfFunds,
            user_id: userId,
            user_email: selectedUserForNewAccount,
            percent_share: accountDistributions.length === 0 ? '100.00' : '0.00'
          }
        ]);
      }

      setSelectedUserForNewAccount('');
      setSelectedAccountId('');
      setNewAccountNumber('');
      setNewSourceOfFunds('');
      setIsNewAccount(false);
      setSelectedUserAccounts([]);

      toast.success('Account added to project');
    } catch (error) {
      console.error('Error adding account:', error);
      toast.error(error.response?.data?.message || 'Failed to add account');
    }
  };

  // Remove account from distributions (by index)
  const handleRemoveAccountFromDistribution = (index) => {
    setAccountDistributions(accountDistributions.filter((_, i) => i !== index));
  };

  // Handle facility selection for account mapping
  const handleFacilityChange = (facilityId) => {
    const facility = facilities.find(f => f._id === facilityId);
    setAccountMapping({
      facility_id: facilityId,
      account_number: ''
    });
    setSelectedFacilityAccounts(facility?.accounts_receivable || []);
  };

  // Calculate totals
  const calculateChamberTotal = () => {
    return chamberUsage
      .filter(item => !excludedChamberRows.includes(item.uniqueKey))
      .reduce((sum, item) => sum + (item.total_cost || 0), 0);
  };

  const calculateResourceTotal = () => {
    return resourceUsage
      .filter(item => !excludedResourceRows.includes(item.uniqueKey))
      .reduce((sum, item) => sum + (item.total_cost || 0), 0);
  };

  const calculateAdjustmentsTotal = () => {
    return adjustments.reduce((sum, adj) => sum + (parseFloat(adj.amount) || 0), 0);
  };

  const calculatePreviousBalanceTotal = () => {
    return previousBalances
      .filter(pb => pb.included)
      .reduce((sum, pb) => sum + (pb.remaining_amount || 0), 0);
  };

  const calculateCurrentPeriodAmount = () => {
    return calculateChamberTotal() + calculateResourceTotal() + calculateAdjustmentsTotal();
  };

  const calculateTotalAmount = () => {
    return calculateCurrentPeriodAmount() + calculatePreviousBalanceTotal();
  };

  // Save invoice changes
  const handleSaveInvoice = async () => {
    try {
      // Amount distribution is now user-driven (set by the PI/payers after the
      // invoice is sent), so there is no admin-side split to validate here.
      setLoading(true);
      
      // Prepare chamber usage data (only included rows)
      const chamberUsageData = chamberUsage
        .filter(item => !excludedChamberRows.includes(item.uniqueKey))
        .map(item => ({
          chamber_id: item.chamber_id,
          assignment_id: item.assignment_id,
          billed_period_id: item.billed_period_id, // Include for precise identification
          rate_id: item.rate_id,
          start_date: item.start_date,
          end_date: item.end_date,
          quantity: item.quantity,
          total_days: item.total_days,
          rate: item.rate,
          rate_basis: item.rate_basis,
          total_cost: item.total_cost
        }));

      // Prepare resource usage data (only included rows)
      const resourceUsageData = resourceUsage
        .filter(item => !excludedResourceRows.includes(item.uniqueKey))
        .map(item => ({
          resource_id: item.resource_id,
          quantity: item.quantity,
          rate: item.rate,
          total_cost: item.total_cost
        }));

      // Prepare previous balance data
      const previousBalanceData = previousBalances
        .filter(balance => balance.included)
        .map(balance => ({
          invoice_id: balance.invoice_id,
          original_amount: balance.original_amount,
          amount_paid: balance.amount_paid,
          remaining_amount: balance.remaining_amount
        }));

      // Prepare account mapping
      const accountMappingData = (accountMapping.facility_id && accountMapping.account_number) 
        ? {
            facility_id: accountMapping.facility_id,
            account_number: accountMapping.account_number
          }
        : null;

      const totalAmount = calculateTotalAmount();
      const currentPeriodAmount = calculateCurrentPeriodAmount();

      // Update the invoice
      const response = await axios.put(`/api/invoices/${project.project_id}/${invoice.invoice_id}`, {
        bill_start_date: billStartDate,
        bill_end_date: billEndDate,
        bill_to: billTo,
        comment: invoiceComment,
        chamber_usage: chamberUsageData,
        resource_usage: resourceUsageData,
        adjustments: adjustments,
        previous_balance: previousBalanceData,
        // Distribution is no longer set by admins; the PI/payers set it after the
        // invoice is sent to users.
        account_mapping: accountMappingData,
        total_amount: totalAmount,
        current_period_amount: currentPeriodAmount
      });

      if (response.data.success) {
        toast.success('Invoice updated successfully');
        if (onInvoiceUpdated) {
          onInvoiceUpdated(response.data.invoice);
        }
        onClose();
      } else {
        toast.error(response.data.message || 'Failed to update invoice');
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error('Failed to update invoice: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setInvoiceData(null);
      setBillStartDate('');
      setBillEndDate('');
      setBillTo({
        name: '',
        company: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: ''
      });
      setSelectedBillToUser('');
      setInvoiceComment('');
      setAdjustments([]);
      setDistributionType('user_based');
      setUserDistributions([]);
      setAccountDistributions([]);
      setChamberUsage([]);
      setResourceUsage([]);
      setExcludedChamberRows([]);
      setExcludedResourceRows([]);
      setPreviousBalances([]);
      setAccountMapping({ facility_id: '', account_number: '' });
      setSelectedUserToAdd('');
      setNewAccountNumber('');
      setNewSourceOfFunds('');
      setSelectedUserForNewAccount('');
      setSelectedUserAccounts([]);
      setLoadingUserAccounts(false);
      setSelectedAccountId('');
      setIsNewAccount(false);
      setCurrentProject(null);
    }
  }, [open]);

  if (!invoice || !project) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5">
          Edit Invoice: {invoice.invoice_id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Project: {project.project_id} - {project.project_title}
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        {loadingInvoiceData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Warning about payments */}
            {invoiceData?.amount_paid > 0 && (
              <Alert severity="warning">
                This invoice has payments totaling ${invoiceData.amount_paid.toFixed(2)}. 
                Editing will affect the remaining balance calculation.
              </Alert>
            )}

            {/* Billing Period Section */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1, mb: 2 }}>
                Billing Period
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Bill Start Date"
                    value={billStartDate}
                    onChange={(e) => handleBillStartDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Changing this updates all space usage start dates"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Bill End Date"
                    value={billEndDate}
                    onChange={(e) => handleBillEndDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Changing this updates all space usage end dates"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Bill To Section */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1, mb: 2 }}>
                Bill To
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select Recipient</InputLabel>
                    <Select
                      value={selectedBillToUser}
                      onChange={(e) => handleBillToUserSelect(e.target.value)}
                      label="Select Recipient"
                      disabled={loadingBillToUser}
                    >
                      <MenuItem value="">
                        <em>Select a project user</em>
                      </MenuItem>
                      {(project?.users || [])
                        .filter(u => !u.isDeleted)
                        .map((user, index) => (
                          <MenuItem key={index} value={user.email}>
                            {user.first_name} {user.last_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={billTo.name}
                    onChange={(e) => setBillTo({ ...billTo, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel shrink>Phone</InputLabel>
                  <PhoneInput
                    value={billTo.phone}
                    onChange={(v) => setBillTo({ ...billTo, phone: v })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={billTo.email}
                    onChange={(e) => setBillTo({ ...billTo, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company/Organization"
                    value={billTo.company}
                    onChange={(e) => setBillTo({ ...billTo, company: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={billTo.address_line1}
                    onChange={(e) => setBillTo({ ...billTo, address_line1: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={billTo.address_line2}
                    onChange={(e) => setBillTo({ ...billTo, address_line2: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="City"
                    value={billTo.city}
                    onChange={(e) => setBillTo({ ...billTo, city: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="State"
                    value={billTo.state}
                    onChange={(e) => setBillTo({ ...billTo, state: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={billTo.zip}
                    onChange={(e) => setBillTo({ ...billTo, zip: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Chamber Usage Section */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1, mb: 2 }}>
                Compartment Usage
              </Typography>
              {chamberUsage.length === 0 ? (
                <Typography color="text.secondary">No compartment usage for this invoice</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Compartment</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell align="right">Days</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Rate/Day</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chamberUsage.map((item) => {
                        const isExcluded = excludedChamberRows.includes(item.uniqueKey);
                        return (
                          <TableRow 
                            key={item.uniqueKey}
                            sx={{ 
                              opacity: isExcluded ? 0.5 : 1,
                              backgroundColor: isExcluded ? '#f5f5f5' : 'inherit',
                              textDecoration: isExcluded ? 'line-through' : 'none'
                            }}
                          >
                            <TableCell>
                              {item.chamber_name}
                              {isAssignmentActive(item.assignment_status) ? (
                                <Chip label="Active" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                              ) : (
                                <Chip label="Completed" size="small" color="default" variant="outlined" sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell>{item.rate_name || item.rate_id}</TableCell>
                            <TableCell>
                              <TextField
                                type="date"
                                size="small"
                                value={item.start_date}
                                onChange={(e) => handleChamberUsageChange(item.uniqueKey, 'start_date', e.target.value)}
                                disabled={isExcluded}
                                sx={{ width: 150 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="date"
                                size="small"
                                value={item.end_date}
                                onChange={(e) => handleChamberUsageChange(item.uniqueKey, 'end_date', e.target.value)}
                                disabled={isExcluded}
                                sx={{ width: 150 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {item.total_days}
                              {formatBilledUnits(item.rate_basis, billedUnits(item.rate_basis, item.start_date, item.end_date)) && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {formatBilledUnits(item.rate_basis, billedUnits(item.rate_basis, item.start_date, item.end_date))}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{formatRate(item.rate)} / {item.rate_basis || 'day'}</TableCell>
                            <TableCell align="right">${item.total_cost?.toFixed(2)}</TableCell>
                            <TableCell align="center">
                              <IconButton aria-label="Restore"
                                size="small"
                                onClick={() => toggleChamberRowExclusion(item.uniqueKey)}
                                color={isExcluded ? 'primary' : 'error'}
                                title={isExcluded ? 'Include in invoice' : 'Exclude from invoice'}
                              >
                                {isExcluded ? <RestoreIcon /> : <DeleteIcon />}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={7} align="right"><strong>Compartment Total:</strong></TableCell>
                        <TableCell align="right"><strong>${calculateChamberTotal().toFixed(2)}</strong></TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Resource Usage Section */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1, mb: 2 }}>
                Resource Usage
              </Typography>
              {resourceUsage.length === 0 ? (
                <Typography color="text.secondary">No resource usage for this invoice</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Resource Name</TableCell>
                        <TableCell>Rate Type</TableCell>
                        <TableCell align="right">Units</TableCell>
                        <TableCell align="right">Rate/Unit</TableCell>
                        <TableCell align="right">Total Cost</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resourceUsage.map((item) => {
                        const isExcluded = excludedResourceRows.includes(item.uniqueKey);
                        const unitsDisplay = item.quantity != null ? `${item.quantity} (${item.rate_basis || 'unit'})` : '—';
                        return (
                          <TableRow
                            key={item.uniqueKey}
                            sx={{
                              opacity: isExcluded ? 0.5 : 1,
                              backgroundColor: isExcluded ? '#f5f5f5' : 'inherit'
                            }}
                          >
                            <TableCell>{item.resource_name}</TableCell>
                            <TableCell>{item.rate_display_name || '—'}</TableCell>
                            <TableCell align="right">{unitsDisplay}</TableCell>
                            <TableCell align="right">${item.rate?.toFixed(2)}</TableCell>
                            <TableCell align="right">${item.total_cost?.toFixed(2)}</TableCell>
                            <TableCell align="center">
                              <IconButton aria-label="Restore"
                                size="small"
                                onClick={() => toggleResourceRowExclusion(item.uniqueKey)}
                                color={isExcluded ? 'primary' : 'error'}
                                title={isExcluded ? 'Include in invoice' : 'Exclude from invoice'}
                              >
                                {isExcluded ? <RestoreIcon /> : <DeleteIcon />}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={4} align="right"><strong>Resource Total:</strong></TableCell>
                        <TableCell align="right"><strong>${calculateResourceTotal().toFixed(2)}</strong></TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Adjustments Section */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1 }}>
                  Adjustments
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddAdjustment}
                  size="small"
                >
                  Add Adjustment
                </Button>
              </Box>
              {adjustments.length === 0 ? (
                <Typography color="text.secondary">No adjustments</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {adjustments.map((adj, index) => (
                    <Grid container spacing={2} key={index} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={adj.amount}
                          onChange={(e) => handleAdjustmentChange(index, 'amount', e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <TextField
                          fullWidth
                          label="Description"
                          value={adj.comments}
                          onChange={(e) => handleAdjustmentChange(index, 'comments', e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <IconButton aria-label="Delete" onClick={() => handleRemoveAdjustment(index)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Typography variant="body1" align="right">
                    <strong>Adjustments Total: ${calculateAdjustmentsTotal().toFixed(2)}</strong>
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Payment Distribution is now user-driven: the project PI and payers
                select accounts and percentage splits after the invoice is sent.
                Admins no longer set the split here. */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1, mb: 2 }}>
                Payment Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Accounts and percentage splits are selected by the project PI and payers
                after the invoice is finalized and sent to users. You no longer set them here.
              </Typography>
            </Paper>

            {false && (
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1, mb: 2 }}>
                Amount Distribution
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Distribution Type</InputLabel>
                    <Select
                      value={distributionType}
                      onChange={(e) => setDistributionType(e.target.value)}
                      label="Distribution Type"
                    >
                      <MenuItem value="user_based">User Based</MenuItem>
                      <MenuItem value="account_based">Account Based</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              {/* Add User/Account Section */}
              {distributionType === 'user_based' ? (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Add User to Distribution</Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select User</InputLabel>
                        <Select
                          value={selectedUserToAdd}
                          onChange={(e) => setSelectedUserToAdd(e.target.value)}
                          label="Select User"
                        >
                          <MenuItem value="">
                            <em>Select a user to add</em>
                          </MenuItem>
                          {allUsers
                            .filter(user => !userDistributions.some(dist => {
                              const distUserId = typeof dist.user_id === 'string' ? dist.user_id : dist.user_id?._id;
                              return distUserId === user._id;
                            }))
                            .map(user => (
                              <MenuItem key={user._id} value={user._id}>
                                {user.first_name} {user.last_name} ({user.email})
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddUserToDistribution}
                        disabled={!selectedUserToAdd}
                        fullWidth
                      >
                        Add User
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Available from project:</Typography>
                    {((currentProject || project)?.funding || []).map((funding, index) => {
                      const proj = currentProject || project;
                      const projectUser = funding.user_email
                        ? proj?.users?.find(u => u.email === funding.user_email)
                        : funding.user_id
                          ? proj?.users?.find(
                              u => (u.user?._id || u.user)?.toString() === funding.user_id?.toString()
                            )
                          : null;
                      const userId = funding.user_id || projectUser?.user?._id || projectUser?.user;
                      const userEmail = funding.user_email || projectUser?.email;
                      return (
                        <Button
                          key={`funding-${index}`}
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            handleQuickAddFundingAccount(
                              funding.account_number,
                              funding.source_of_funds,
                              userId,
                              userEmail
                            )
                          }
                          disabled={accountDistributions.some(dist => {
                            const distUserMatch = funding.user_email
                              ? dist.user_email === funding.user_email
                              : funding.user_id &&
                                dist.user_id &&
                                dist.user_id.toString() === funding.user_id.toString();
                            return (
                              dist.account_number === funding.account_number && distUserMatch
                            );
                          })}
                        >
                          {displayAccountNumber(funding.account_number)}
                        </Button>
                      );
                    })}
                  </Box>

                  <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Add account from a user profile or create new
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Project user</InputLabel>
                          <Select
                            value={selectedUserForNewAccount}
                            onChange={(e) => handleUserSelectionForNewAccount(e.target.value)}
                            label="Project user"
                          >
                            <MenuItem value="">
                              <em>Select a user</em>
                            </MenuItem>
                            {((currentProject || project)?.users || [])
                              .filter(u => !u.isDeleted)
                              .map(u => (
                                <MenuItem key={u.email} value={u.email}>
                                  {u.first_name} {u.last_name} ({u.email})
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {selectedUserForNewAccount && (
                        <Grid item xs={12} md={4}>
                          {loadingUserAccounts ? (
                            <CircularProgress size={24} />
                          ) : (
                            <FormControl fullWidth size="small">
                              <InputLabel>Account</InputLabel>
                              <Select
                                value={selectedAccountId}
                                onChange={(e) => handleAccountSelection(e.target.value)}
                                label="Account"
                              >
                                <MenuItem value="">
                                  <em>Select an account</em>
                                </MenuItem>
                                {selectedUserAccounts.map(acc => (
                                  <MenuItem key={acc._id} value={acc._id}>
                                    {acc.number} — {acc.source}
                                  </MenuItem>
                                ))}
                                <MenuItem value="new">+ Add new account</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                      )}
                      {selectedUserForNewAccount &&
                        (isNewAccount || selectedAccountId === 'new' || selectedAccountId) && (
                          <>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Account number"
                                value={newAccountNumber}
                                onChange={(e) => setNewAccountNumber(e.target.value)}
                                disabled={!isNewAccount && selectedAccountId !== 'new'}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Source of funds"
                                value={newSourceOfFunds}
                                onChange={(e) => setNewSourceOfFunds(e.target.value)}
                                disabled={!isNewAccount && selectedAccountId !== 'new'}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddAccountToProject}
                                disabled={
                                  !selectedUserForNewAccount || !newAccountNumber || !newSourceOfFunds
                                }
                                fullWidth
                              >
                                {isNewAccount || selectedAccountId === 'new'
                                  ? 'Save and add'
                                  : 'Add to project and distribution'}
                              </Button>
                            </Grid>
                          </>
                        )}
                    </Grid>
                  </Box>
                </>
              )}
              
              <Box sx={{ mt: 2 }}>
                {distributionType === 'user_based' ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell align="right">Percentage (%)</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userDistributions.map((dist, index) => {
                          const userId = typeof dist.user_id === 'string' ? dist.user_id : dist.user_id?._id;
                          const amount = (calculateTotalAmount() * (parseFloat(dist.percent_share) || 0)) / 100;
                          return (
                            <TableRow key={index}>
                              <TableCell>{dist.user_name}</TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={dist.percent_share}
                                  onChange={(e) => handleUserDistributionChange(userId, e.target.value)}
                                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                                  sx={{ width: 100 }}
                                />
                              </TableCell>
                              <TableCell align="right">${amount.toFixed(2)}</TableCell>
                              <TableCell align="center">
                                <IconButton aria-label="Remove from distribution"
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveUserFromDistribution(userId)}
                                  title="Remove from distribution"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell align="right">
                            <strong>
                              {userDistributions.reduce((sum, dist) => sum + (parseFloat(dist.percent_share) || 0), 0).toFixed(2)}%
                            </strong>
                          </TableCell>
                          <TableCell align="right"><strong>${calculateTotalAmount().toFixed(2)}</strong></TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Account</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell align="right">Percentage (%)</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {accountDistributions.map((dist, index) => {
                          const amount = (calculateTotalAmount() * (parseFloat(dist.percent_share) || 0)) / 100;
                          // Existing dist rows (loaded from the server) lock their
                          // account_number / source_of_funds. Admins can only
                          // change the percent share or remove the row entirely.
                          // To change the account, remove this row and add a new one.
                          const isExisting = Boolean(dist._isExisting);
                          return (
                            <TableRow
                              key={`${dist._id || dist.user_email || dist.user_id || 'row'}-${dist.account_number}-${index}`}
                            >
                              <TableCell>
                                {isExisting ? (
                                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                                    {dist.account_number}
                                  </Typography>
                                ) : (
                                  <TextField
                                    size="small"
                                    value={dist.account_number}
                                    onChange={(e) =>
                                      handleAccountDistributionFieldChange(
                                        index,
                                        'account_number',
                                        e.target.value
                                      )
                                    }
                                    sx={{ minWidth: 120 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {isExisting ? (
                                  <Typography variant="body2" sx={{ minWidth: 140 }}>
                                    {dist.source_of_funds}
                                  </Typography>
                                ) : (
                                  <TextField
                                    size="small"
                                    value={dist.source_of_funds}
                                    onChange={(e) =>
                                      handleAccountDistributionFieldChange(
                                        index,
                                        'source_of_funds',
                                        e.target.value
                                      )
                                    }
                                    sx={{ minWidth: 140 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={dist.percent_share}
                                  onChange={(e) =>
                                    handleAccountDistributionChange(index, e.target.value)
                                  }
                                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                                  sx={{ width: 100 }}
                                />
                              </TableCell>
                              <TableCell align="right">${amount.toFixed(2)}</TableCell>
                              <TableCell align="center">
                                <IconButton aria-label="Remove from distribution"
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveAccountFromDistribution(index)}
                                  title="Remove from distribution"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell colSpan={2}><strong>Total</strong></TableCell>
                          <TableCell align="right">
                            <strong>
                              {accountDistributions.reduce((sum, dist) => sum + (parseFloat(dist.percent_share) || 0), 0).toFixed(2)}%
                            </strong>
                          </TableCell>
                          <TableCell align="right"><strong>${calculateTotalAmount().toFixed(2)}</strong></TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Paper>
            )}

            {/* Comment Section */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1, mb: 2 }}>
                Invoice Comment
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comment (appears on PDF)"
                value={invoiceComment}
                onChange={(e) => setInvoiceComment(e.target.value)}
              />
            </Paper>

            {/* Invoice Summary */}
            <Paper elevation={2} sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Invoice Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography>Compartment Usage:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right">${calculateChamberTotal().toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Resource Usage:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right">${calculateResourceTotal().toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Adjustments:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right">${calculateAdjustmentsTotal().toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Current Period Amount:</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right"><strong>${calculateCurrentPeriodAmount().toFixed(2)}</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Previous Balance:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right">${calculatePreviousBalanceTotal().toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6"><strong>Total Amount:</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" align="right"><strong>${calculateTotalAmount().toFixed(2)}</strong></Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveInvoice}
          disabled={loading || loadingInvoiceData}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInvoice;
